import { useState, useEffect } from 'react';
import {
  Button,
  ButtonSet,
  Slider,
  Tooltip,
  ContentSwitcher,
  Switch,
  InlineNotification,
  ModalWrapper,
} from 'carbon-components-react';
import { Close32 } from '@carbon/icons-react';
import { useIntl, FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './RPAPriceEstimator.scss';
import './NoteDrawer.scss';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentTracking } from '../lib/tracker';
import NoteDrawer from './NoteDrawer';
import Footnote from './Footnote';
import Thanks from './Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import messages from '../locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { setPrice } from '../hooks/usePrice';

import {
  environments,
  unattendedRobots,
  attendedRobots,
  defaultConfiguration,
  Product,
  DisplayName,
  limits,
  UT30RPAPE,
} from '../constants/defaults';
import {
  determineCountry,
  getMessage,
  LangDefaults,
  useLang,
} from 'src/common/hooks/widgets/useLang';

import countryList from '../data/currencies.json';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';

export interface RPAPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export interface roiCookieContent {
  currentNumberOfHoursPerWeek: number;
  currentFullTimeEmployeeCost: number;
  errorRate: number;
  numberOfProcessToAutomate: number;
  estimate: string;
}

export const cookieContentObject: roiCookieContent = {
  currentNumberOfHoursPerWeek: 0,
  currentFullTimeEmployeeCost: 0,
  errorRate: 0,
  numberOfProcessToAutomate: 0,
  estimate: '',
};

/**
 *
 */
const RPAPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: RPAPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [expandingConfiguration, setExpandingConfiguration] = useState('No');
  const [productToAcquire, setProductToAcquire] = useState(Product.SaaS);
  const [configurationSize, setConfigurationSize] = useState('Medium');
  const [numberOfEnvironments, setNumberOfEnvironments] = useState(
    environments.min,
  );
  const [roiNumberEnvironments, setNumberEnvironments] = useState(
    environments.min,
  );
  const [numberOfUnattendedRobots, setNumberOfUnattendedRobots] = useState(
    unattendedRobots.min,
  );
  const [roiUnattendedBots, setRoiUnattendedBots] = useState(
    unattendedRobots.min,
  );
  const [numberOfAttendedRobots, setNumberOfAttendedRobots] = useState(
    attendedRobots.min,
  );
  const [roiAttendedRobots, setRoiAttendedRobots] = useState(
    attendedRobots.min,
  );
  const [showContactUs, setShowContactUs] = useState(true);
  const [invalidVMMax, setInvalidVMMax] = useState(false);
  const [invalidVMMin, setInvalidVMMin] = useState(false);
  const [invalidURMax, setInvalidURMax] = useState(false);
  const [invalidURMin, setInvalidURMin] = useState(false);
  const [invalidARMax, setInvalidARMax] = useState(false);
  const [invalidARMin, setInvalidARMin] = useState(false);
  const [switchIndex, setSwitchIndex] = useState(defaultConfiguration);
  const [urxLoaded, setUrxLoaded] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const bpConsentMail = false;
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const { bpName, companyId, logo, desc } = useBPName(UT30RPAPE, true);
  const cookieURL = useCookiesURL();
  const intl = useIntl();
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');

  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  // Set us-en
  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  const [monthlyPrice, setMonthly] = useCurrency(currency, userLocale, 0);
  // Cost to use in ROI
  const [cost, setCost] = useState(0);

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const [showForm, setShowForm] = useState(false);

  const [cookieContent, setCookieContent] = useState(cookieContentObject);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
  );
  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const vmsId = document.getElementById('VMs-id-input-for-slider');
    const urId = document.getElementById('Ur-id-input-for-slider');
    const arId = document.getElementById('Ar-id-input-for-slider');

    const toggle = document.getElementById('ic-toogle-id') as HTMLInputElement;
    /**
     * Define Listener
     */
    const addListener = (
      id: HTMLElement | null,
      setMax: React.Dispatch<React.SetStateAction<boolean>>,
      setMin: React.Dispatch<React.SetStateAction<boolean>>,
      max: number,
      min: number,
      setValue: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e != null) {
            let minValue = min;
            if (toggle?.checked === true) {
              minValue = 0;
            }
            const element = e.currentTarget as HTMLInputElement;
            if (element.value !== '') {
              if (Number.parseInt(element.value) > max) {
                element.value = max.toString();
                setMax(true);
              } else if (Number.parseInt(element.value) < minValue) {
                element.value = minValue.toString();
                setMin(true);
              }
            }
          } else {
            setValue(min);
          }
        });
      }
    };

    /**
     * Number of Environments
     */
    addListener(
      vmsId,
      setInvalidVMMax,
      setInvalidVMMin,
      environments.max,
      environments.min,
      setNumberOfEnvironments,
    );

    /**
     * Unattended Robot
     */
    addListener(
      urId,
      setInvalidURMax,
      setInvalidURMin,
      unattendedRobots.max,
      unattendedRobots.min,
      setNumberOfUnattendedRobots,
    );

    /**
     * Unattended Robot
     */
    addListener(
      arId,
      setInvalidARMax,
      setInvalidARMin,
      unattendedRobots.max,
      unattendedRobots.min,
      setNumberOfAttendedRobots,
    );
  }, []);

  /**
   * Read and set defaults from ROI
   */
  const startTxnProcessor = () => {
    try {
      setTimeout(async () => {
        // Check to open the form
        const showBenefit = localStorage.getItem('showRPAForm');
        if (showBenefit !== null) {
          if (showBenefit === 'true') {
            setShowForm(true);
          }
        }
        // Check inputs from ROI
        const arId = localStorage.getItem('roiAttendedRobots-Id');
        if (arId !== null) {
          const value = Number.parseInt(arId);
          setRoiAttendedRobots(value);
        }
        const urId = localStorage.getItem('roiUnattendedBots-Id');
        if (urId !== null) {
          const value = Number.parseInt(urId);
          setRoiUnattendedBots(value);
        }
        const neId = localStorage.getItem('roiNumberEnvironments-Id');
        if (neId !== null) {
          const value = Number.parseInt(neId);
          setNumberEnvironments(value);
        }
        startTxnProcessor();
      }, limits.secs * 1000);
    } catch (err) {
      console.error(`Error while timeout: ${err}`);
    }
  };

  startTxnProcessor();

  /**
   *
   */
  useEffect(() => {
    const showBenefit = localStorage.getItem('showRPAForm');
    if (showBenefit !== null) {
      if (showBenefit === 'true') {
        setCookie();
        initUrxForm(
          environment,
          URX_FORM.INSTANCE_ID,
          URX_FORM.FORM_ID,
          urxlang as IBMLocale,
          companyId,
        );
        setDrawerOpen(true);
        setShowForm(false);
        localStorage.setItem('showRPAForm', 'false');
      }
    }
    // Check inputs from ROI
    const arId = localStorage.getItem('roiAttendedRobots-Id');
    if (arId !== null) {
      const value = Number.parseInt(arId);
      setNumberOfAttendedRobots(value);
    }
    const urId = localStorage.getItem('roiUnattendedBots-Id');
    if (urId !== null) {
      const value = Number.parseInt(urId);
      setNumberOfUnattendedRobots(value);
    }
    const neId = localStorage.getItem('roiNumberEnvironments-Id');
    if (neId !== null) {
      const value = Number.parseInt(neId);
      setNumberOfEnvironments(value);
    }
  }, [showForm, roiAttendedRobots, roiUnattendedBots, roiNumberEnvironments]);

  /**
   *
   */
  useEffect(() => {
    setCookie();
    localStorage.setItem('pe-Id', cost.toString());
  }, [
    numberOfEnvironments,
    numberOfUnattendedRobots,
    expandingConfiguration,
    cost,
    bpConsentMail,
  ]);

  /**
   *
   */
  const setCookie = () => {
    const jsonCookie = localStorage.getItem('RPA-ROI-formContent');
    let cookieContent = '';
    if (jsonCookie !== null) {
      const jsonFileToProcess = JSON.parse(jsonCookie);
      cookieContent = jsonFileToProcess.message;
      setCookieContent(jsonFileToProcess.content);
    }
    const text = JSON.stringify({
      source: URX_FORM.FORM_ID,
      // One field to provide all values (stringify)
      dcq: {
        Q_NOTESDATA:
          cookieURL +
          ' | Number of Environments: ' +
          numberOfEnvironments +
          ' | Number of unattended robots: ' +
          numberOfUnattendedRobots +
          ' | Number of attended robots: ' +
          numberOfAttendedRobots +
          ' | Product to acquire: ' +
          productToAcquire +
          ' | Estimated monthly cost: ' +
          monthlyPrice +
          ' | ROI feed: ' +
          cookieContent +
          ' | Request a quote -IBM RPA Estimator-' +
          ' | FormId:' +
          URX_FORM.FORM_ID,
        Q_ASSET_NAME: 'IBM RPA Estimator', // ISC - Asset Name
      },
    });
    Cookies.set('urxdcq', text);
  };

  /* Have urx form deault in us-en when on language us page */
  useEffect(() => {
    const fetchedUrxLang = localeCode; // us-en
    setUrxLang(userLocale as IBMLocale); // de-de
    if (fetchedUrxLang !== lang) {
      setUrxLang(fetchedUrxLang as IBMLocale); // default us-en
    }
  });

  /**
   *
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
  ) => {
    if (value >= min && value <= max) {
      setValue(value);
    } else if (value >= max) {
      setValue(max);
    } else {
      setValue(min);
    }
  };

  /**
   *
   */
  useEffect(() => {
    switch (switchIndex) {
      case 0: {
        setNumberOfEnvironments(1);
        setNumberOfAttendedRobots(10);
        setNumberOfUnattendedRobots(2);
        setConfigurationSize('Small');
        break;
      }
      case 1: {
        setNumberOfEnvironments(2);
        setNumberOfAttendedRobots(50);
        setNumberOfUnattendedRobots(10);
        setConfigurationSize('Medium');
        break;
      }
      case 2: {
        setNumberOfEnvironments(3);
        setNumberOfAttendedRobots(250);
        setNumberOfUnattendedRobots(50);
        setConfigurationSize('Large');
        break;
      }
      case 3: {
        setNumberOfEnvironments(1);
        setNumberOfUnattendedRobots(1);
        setNumberOfAttendedRobots(2);
        break;
      }
    }
  }, [switchIndex, configurationSize]);

  /**
   * Call to start the pricing
   */
  setPrice(
    numberOfUnattendedRobots,
    numberOfEnvironments,
    numberOfAttendedRobots,
    productToAcquire,
    expandingConfiguration,
    setShowContactUs,
    setMonthly,
    currency,
    country,
    userLocale,
    setCost,
  );

  return (
    <section id="RPA_PE" className="widget-styles">
      <div className="RPA_PE dsh-widget widget-reset">
        <Helmet>
          <script src={urxScript}></script>
          <script src={urxScriptLoader}></script>
        </Helmet>
        {formComplete ? (
          <Thanks
            productToAcquire={DisplayName[productToAcquire]}
            numberOfUnattendedRobots={numberOfUnattendedRobots}
            numberOfAttendedRobots={numberOfAttendedRobots}
            numberOfEnvironments={numberOfEnvironments}
            montlyCost={monthlyPrice}
            cookieContent={cookieContent}
            bpName={bpName}
            bpConsentMail={false}
          ></Thanks>
        ) : (
          // Form not completed
          <div className="bx--grid" id="RPA-PE-Grid">
            {bpName ? (
              <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
            ) : null}
            <div className="bx--row mar3">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                <div className="bx--row">
                  <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12">
                    {localExist && toggleCountry ? (
                      <ModalWrapper
                        modalLabel={` ${getMessage(
                          messages.foundCountry,
                        )} ${modalLocalCountry}.
                            ${getMessage(
                              messages.accessPage,
                            )} ${modalLocalLanguage} ${modalLocalCurrency}
                            `}
                        modalHeading={`${getMessage(messages.availableIn)}
                            ${modalLocalLanguage} ${getMessage(
                          messages.localCurrencie,
                        )} ${modalLocalCurrency}`}
                        size={'sm'}
                        primaryButtonText={`${getMessage(
                          messages.takeMeThere,
                        )}`}
                        secondaryButtonText={`${getMessage(
                          messages.stayHere,
                        )} `}
                        buttonTriggerText={` ${getMessage(
                          messages.accessPageCapitalized,
                        )} ${modalLocalLanguage}`}
                        handleOpen={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'RPA Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Modal open ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        handleSubmit={() => {
                          localStorage.setItem('langLocale', selectedLocale);
                          (window.location.href =
                            'https://www.ibm.com/' +
                            selectedLocale +
                            '/products/robotic-process-automation/pricing'),
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Switched to country ' + modalLocalCountry,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          return true;
                        }}
                        onSecondarySubmit={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'RPA Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA:
                              'Stay here button clicked ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setToggleCountry(false);
                          return true;
                        }}
                      ></ModalWrapper>
                    ) : null}
                  </div>
                </div>

                <div className="bx--row">
                  <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                    <p className="page_sub_heading">
                      <FormattedMessage {...messages.widgetText_1} />
                      {` `}
                      <span className="RPA_PE__bluehighlight">
                        ({DisplayName[productToAcquire]})
                      </span>
                      {` `}
                      <FormattedMessage {...messages.widgetText_3} />
                    </p>
                    <p className="page_body padding_24_3 mar2">
                      <FormattedMessage {...messages.widgetInfo} />
                      {` `}
                      <FormattedMessage {...messages.widgetStatement} />
                    </p>

                    {/* Tooltip: Desired configuration */}
                    <div className="bx--row ">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 spacing-12">
                        <Tooltip
                          direction={'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.productToAcquire}
                              />
                            </span>
                          }
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Acquire tooltip',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <span className="slide_text">
                            <FormattedMessage
                              {...messages.productToAcquireHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.productToAcquireDescTooltip}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {/* ContentSwitcher: Desired configuration */}
                    <div className="bx--row mar1">
                      <div className="bx--col-lg-8 bx--col-md-4 bx--col-sm-16">
                        <ContentSwitcher
                          selectionMode="manual"
                          onChange={(obj) => {
                            const { text } = obj;
                            if (text !== undefined) {
                              setProductToAcquire(
                                text === Product.SaaS
                                  ? Product.SaaS
                                  : Product.Software,
                              );
                            }
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing page',
                              resultValue: `CTA clicked ${text}`,
                              CTA: `Acquire product selected`,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <Switch name={`${Product.SaaS}`} text="SaaS" />
                          <Switch
                            name={`${Product.Software}`}
                            text="On-Premises"
                          />
                        </ContentSwitcher>
                      </div>
                    </div>
                    {/* Tooltip: Configuration Size */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-16 spacing-12">
                        <Tooltip
                          direction={'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.configurationSizeHeadTooltip}
                              />
                            </span>
                          }
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Configuration size tooltip',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <span className="slide_text">
                            <FormattedMessage
                              {...messages.configurationSizeHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.configurationSizeDescTooltip}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {/* ContentSwitcher: Configuration Size */}
                    <div className="bx--row  mar1">
                      <div className="bx--col-lg-10 bx--col-md-6 bx--col-sm-16">
                        <ContentSwitcher
                          className="RPA_PE__switcher-config"
                          selectionMode="manual"
                          selectedIndex={switchIndex}
                          onChange={(obj) => {
                            const { index, text } = obj;
                            setSwitchIndex(
                              index == undefined ? defaultConfiguration : index,
                            );
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing page',
                              resultValue: `CTA clicked ${text}`,
                              CTA: `Configuration size switcher`,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <Switch name="Small" text="Small" />
                          <Switch name="Medium" text="Medium" />
                          <Switch name="Large" text="Large" />
                          <Switch name="Custom" text="Custom" />
                        </ContentSwitcher>
                      </div>
                    </div>
                    {/* Tooltip: Number of Environments */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of environments tooltip',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.numberOfEnvironments}
                              />
                            </span>
                          }
                        >
                          <span className="slide_text">
                            <FormattedMessage
                              {...messages.numberOfEnvironmentsHeadTooltip}
                            />

                            <br />
                            <FormattedMessage
                              {...messages.numberOfEnvironmentsDescTooltip}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {/* Slider: number of Environments */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="VMs-id"
                          min={
                            expandingConfiguration === 'No'
                              ? environments.min
                              : 0
                          }
                          max={environments.max}
                          inputType={'number'}
                          value={numberOfEnvironments}
                          invalid={invalidVMMax && invalidVMMin}
                          labelText={
                            invalidVMMax ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${environments.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidVMMin ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {expandingConfiguration === 'No'
                                  ? ` ${environments.min}`
                                  : 0}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onBlur={() => {
                            null;
                          }}
                          onChange={({ value }) => {
                            if (value != numberOfEnvironments) {
                              setSwitchIndex(3);
                            }
                            if (value > environments.max) {
                              setNumberOfEnvironments(environments.max);
                              setInvalidVMMax(true);
                            } else if (
                              value <
                              (expandingConfiguration === 'No'
                                ? environments.min
                                : 0)
                            ) {
                              setNumberOfEnvironments(
                                expandingConfiguration === 'No'
                                  ? environments.min
                                  : 0,
                              );
                              setInvalidVMMin(true);
                            } else {
                              handleSliders(
                                value,
                                setNumberOfEnvironments,
                                environments.max,
                                expandingConfiguration === 'No'
                                  ? environments.min
                                  : 0,
                              );
                              setInvalidVMMax(false);
                              setInvalidVMMin(false);
                            }
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of environments slider',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    {/*  Tooltip: Number of Unattended Robots */}
                    <div className="bx--row mar3">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of unattended robots tooltip',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={isSmall ? 'bottom' : 'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.numberOfUnattendedRobots}
                              />
                            </span>
                          }
                        >
                          <span className="slide_text">
                            <FormattedMessage
                              {...messages.numberOfUnattendedRobotsHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.numberOfUnattendedRobotsDescTooltip}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {/* Slider: number of unattended robots */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="Ur-id"
                          min={
                            expandingConfiguration === 'No'
                              ? unattendedRobots.min
                              : 0
                          }
                          max={unattendedRobots.max}
                          value={numberOfUnattendedRobots}
                          inputType={'number'}
                          invalid={invalidURMax && invalidURMin}
                          labelText={
                            invalidURMax ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${unattendedRobots.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidURMin ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {expandingConfiguration === 'No'
                                  ? ` ${unattendedRobots.min}`
                                  : 0}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onBlur={() => {
                            null;
                          }}
                          onChange={({ value }) => {
                            if (value != numberOfUnattendedRobots) {
                              setSwitchIndex(3);
                            }
                            if (value > unattendedRobots.max) {
                              setNumberOfUnattendedRobots(unattendedRobots.max);
                              setInvalidURMax(true);
                            } else if (
                              value <
                              (expandingConfiguration === 'No'
                                ? unattendedRobots.min
                                : 0)
                            ) {
                              setNumberOfUnattendedRobots(
                                expandingConfiguration === 'No'
                                  ? unattendedRobots.min
                                  : 0,
                              );
                              setInvalidURMin(true);
                            } else {
                              handleSliders(
                                value,
                                setNumberOfUnattendedRobots,
                                unattendedRobots.max,
                                expandingConfiguration === 'No'
                                  ? unattendedRobots.min
                                  : 0,
                              );
                              setInvalidURMax(false);
                              setInvalidURMin(false);
                            }
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of unattended robots slider',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    {/*  Tooltip: Number of Attended Robots */}
                    <div className="bx--row mar3">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of attended robots tooltip',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={isSmall ? 'bottom' : 'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.numberOfAttendedRobots}
                              />
                            </span>
                          }
                        >
                          <span className="slide_text">
                            <FormattedMessage
                              {...messages.numberOfAttendedRobotsHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.numberOfAttendedRobotsDescTooltip}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {/* Slider: number of Attended robots */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="Ar-id"
                          min={
                            expandingConfiguration === 'No'
                              ? attendedRobots.min
                              : 0
                          }
                          max={attendedRobots.max}
                          value={numberOfAttendedRobots}
                          inputType={'number'}
                          invalid={invalidARMax && invalidARMin}
                          labelText={
                            invalidARMax ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${attendedRobots.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidARMin ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {expandingConfiguration === 'No'
                                  ? ` ${attendedRobots.min}`
                                  : 0}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onBlur={() => {
                            null;
                          }}
                          onChange={({ value }) => {
                            if (value != numberOfAttendedRobots) {
                              setSwitchIndex(3);
                            }
                            if (value > attendedRobots.max) {
                              setNumberOfAttendedRobots(attendedRobots.max);
                              setInvalidARMax(true);
                            } else if (
                              value <
                              (expandingConfiguration === 'No'
                                ? attendedRobots.min
                                : 0)
                            ) {
                              setNumberOfAttendedRobots(
                                expandingConfiguration === 'No'
                                  ? attendedRobots.min
                                  : 0,
                              );
                              setInvalidARMin(true);
                            } else {
                              handleSliders(
                                value,
                                setNumberOfAttendedRobots,
                                attendedRobots.max,
                                expandingConfiguration === 'No'
                                  ? attendedRobots.min
                                  : 0,
                              );
                              setInvalidARMax(false);
                              setInvalidARMin(false);
                            }
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing page',
                              resultValue: `CTA clicked ${value}`,
                              CTA: 'Number of attended robots slider',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div id="footnote" className="div-only-lg mar3">
                      <div className="bx--row">
                        <div className="bx--col-lg-16">
                          <Footnote numNote={1} />
                          <div className="footnote_text">
                            <FormattedMessage {...messages.footnoteOne} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bx--col-lg-1"></div>
                  {/* Navigation displaying results  */}
                  <div className="bx--col-lg-5 bx--col-md-7 bx--col-sm-4">
                    <div className="bx--row">
                      <div className="RPA_PE__notif">
                        {/* Info to display*/}
                        {productToAcquire === 'SaaS' ? (
                          <InlineNotification
                            kind="info"
                            role="onprem"
                            title={
                              <FormattedMessage
                                {...messages.inlineTextTitleSaaS}
                              />
                            }
                            subtitle={
                              <FormattedMessage {...messages.inlineTextSaaS} />
                            }
                            statusIconDescription="On-Prem Info"
                            hideCloseButton={true}
                          />
                        ) : (
                          <InlineNotification
                            kind="info"
                            role="oncloud"
                            title={
                              <FormattedMessage
                                {...messages.inlineTextTitleLicense}
                              />
                            }
                            subtitle={
                              <FormattedMessage
                                {...messages.inlineTextLicense}
                              />
                            }
                            statusIconDescription="On-Cloud Info"
                            hideCloseButton={true}
                          />
                        )}
                      </div>
                    </div>
                    <div className="bx--row mar4 RPA_PE__sticky-nav">
                      <div className="RPA_PE__sticky-nav price_table_comp">
                        {/* Table with results  */}
                        <table className="cds--data-table cds--data-table--sm">
                          <thead>
                            <tr>
                              <th scope="col" colSpan={2}>
                                <div className="price_table_comp__heading">
                                  <FormattedMessage {...messages.requiremnet} />
                                </div>
                              </th>
                              <th> </th>
                              <th scope="col">
                                <div className="price_table_comp__heading">
                                  <FormattedMessage {...messages.selection} />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody aria-live="polite">
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.productToAcquireHeadTooltip}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">
                                  {`${DisplayName[productToAcquire]}`}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        {/* Condition if above certain value to contact-us button */}
                        {showContactUs ? (
                          <div id="contact_toggle">
                            <p
                              id="widget.contact_info"
                              className="price_table_comp__disclaimer mar4"
                            >
                              <FormattedMessage {...messages.contactInfo} />
                            </p>
                            <ButtonSet className="RPA_PE__stack-button mar4">
                              <Button
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'RPA Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Contact us button',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                  setCookie();
                                }}
                              >
                                <FormattedMessage {...messages.contactUs} />
                              </Button>
                              &nbsp; &nbsp;
                              <Button
                                size="sm"
                                id="contactus_roi_btn"
                                href="#RPA_ROI"
                                target="_self"
                                rel="noreferrer"
                                kind="secondary"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'RPA Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'View RPA benefits button',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setCookie();
                                }}
                              >
                                <FormattedMessage {...messages.getROIButton} />
                              </Button>
                            </ButtonSet>
                          </div>
                        ) : (
                          //Display Value if condition not met
                          <div id="price_toggle">
                            <p
                              id="widget.estimated-cost"
                              className="RPA_PE__ibm-type-e estmt-cost price_table_comp__bodycopy padding_24_2"
                            >
                              <FormattedMessage {...messages.estimatedCosts} />
                              <sup className="RPA_PE__sup">1</sup>
                            </p>
                            <p className="padding_8 price_table_comp__price">
                              {`${monthlyPrice} *`}
                            </p>
                            <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                              *<FormattedMessage {...messages.asteriskPrice} />
                            </p>
                            <ButtonSet className="RPA_PE__stack-button  btn-set mar4">
                              <Button
                                id="request_btn"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'RPA Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Request a quote now',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                  setCookie();
                                }}
                              >
                                <FormattedMessage
                                  {...messages.getQuoteButton}
                                />
                              </Button>
                              &nbsp; &nbsp;
                              <Button
                                size="sm"
                                id="contactus_roi_btn"
                                href="#RPA_ROI"
                                target="_self"
                                rel="noreferrer"
                                kind="secondary"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'RPA Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'View RPA benefits button',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setCookie();
                                }}
                              >
                                <FormattedMessage {...messages.getROIButton} />
                              </Button>
                            </ButtonSet>
                          </div>
                        )}
                        {/* End display results */}
                      </div>
                      {/* Note related to suggested price  */}
                      <div id="footnote" className="div-only-media">
                        <div className="bx--row mar4">
                          <div className="bx--col-lg-9">
                            <div>
                              <Footnote numNote={1} />
                            </div>
                            <div className="footnote_text">
                              <FormattedMessage {...messages.footnoteOne} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <hr className="RPA_PE__custom-hr" id="hr_row_two" />
              </div>
            </div>
          </div>
        )}
        {/* Drawer when request is being triggered */}
        <NoteDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
        <div className="bx--row padding-row">
          <div
            id="PutDrawerToFront"
            className={`RPA_PE-drawer
        ${drawerOpen ? 'opened' : 'closed'} RPA_PE-drawer-transition`}
          >
            <div className="RPA_PE-drawer-inner-container">
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <div className="RPA_PE-drawer-close-container">
                    <Button
                      kind="ghost"
                      renderIcon={Close32}
                      onClick={() => {
                        setDrawerOpen(false);
                      }}
                    >
                      <FormattedMessage {...messages.CloseText} />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  {bpName ? (
                    <BusinessPartner
                      bpName={bpName}
                      logo={logo}
                      urxForm={true}
                    />
                  ) : null}
                </div>
              </div>
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <div className="RPA-PE-drawer-notes-container">
                    <div id="urx-form-container">
                      <div no-toc id="urx-form"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RPAPriceEstimator;
